import { useRef } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";

import useSalesyApiClient from "@/hooks/useSalesyApiClient";

export const getCampaignListsKey = (campaignId: string, query?: string | null) => [
  "get-campaign-lists",
  campaignId,
  query,
];

type CampaignList = {
  campaignId: string;
  query?: string | null;
};

export const useGetCampaignLists = ({ campaignId, query }: CampaignList) => {
  const { apiClient } = useSalesyApiClient();
  const itemsTotalRef = useRef<number>(0);

  const fetchCampaignLists = async ({ pageParam = 0 }) => {
    const response = await apiClient.predictiveCampaigns.getCampaignLists({
      id: campaignId,
      query: query,
      page: pageParam,
      itemsPerPage: 25,
    });

    if (pageParam === 0 && response.info?.items_total) {
      itemsTotalRef.current = response.info.items_total;
    }

    return response;
  };

  return useInfiniteQuery({
    queryKey: getCampaignListsKey(campaignId, query),
    queryFn: fetchCampaignLists,
    enabled: !!campaignId,
    initialPageParam: 0,
    refetchOnWindowFocus: false,
    getNextPageParam: (lastPage) => {
      const { info } = lastPage || {};
      if (!info) return undefined;

      const itemsTotal = info?.items_total ?? itemsTotalRef.current;
      if (!itemsTotal) return undefined;

      return info.page < Math.ceil(itemsTotal / info.items_per_page) - 1 ? info.page + 1 : undefined;
    },
  });
};
